/**
 * Time bound for device-storage writes (IndexedDB, through the storage wrapper). The score buffer and
 * the sync-attempt counter each serialize their read-modify-writes on a promise chain, so a single
 * write that never settles would freeze every later write for the life of the page. Both chains wrap
 * each write in withTimeout and chain on the guarded promise, never the raw write, so a hung write
 * rejects after `ms` and the next one runs. The timer is cleared however the race settles.
 *
 * An IndexedDB write can't be cancelled: a timed-out write is abandoned and may still land later. The
 * score buffer's queue therefore waits one more `ms` for a write it had already handed to IndexedDB
 * before the next one runs (see withinTab in user-session.service.ts).
 * Network requests are aborted instead — see transport-timeout.helpers.ts.
 */
import { getRemoteConfigNumber } from '../firebase';
import { DEFAULT_STORAGE_TIMEOUT_MS, MIN_TIMEOUT_MS, TIMEOUT_REMOTE_CONFIG_KEYS } from './timeout.constants';

export const actionBufferTimeoutMs = (): number => getRemoteConfigNumber(
  TIMEOUT_REMOTE_CONFIG_KEYS.actionBufferMs, DEFAULT_STORAGE_TIMEOUT_MS, MIN_TIMEOUT_MS,
);

export const syncAttemptsWriteTimeoutMs = (): number => getRemoteConfigNumber(
  TIMEOUT_REMOTE_CONFIG_KEYS.syncAttemptsWriteMs, DEFAULT_STORAGE_TIMEOUT_MS, MIN_TIMEOUT_MS,
);

export const withTimeout = <T>(
  write: Promise<T>,
  ms: number,
  onTimeout: () => Error,
): Promise<T> => {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  const clearTimer = () => {
    if (timerId !== undefined) clearTimeout(timerId);
  };
  const timeout = new Promise<never>((_resolve, reject) => {
    timerId = setTimeout(() => reject(onTimeout()), ms);
  });

  return Promise.race([write, timeout]).then(
    (value) => {
      clearTimer();
      return value;
    },
    (error) => {
      clearTimer();
      throw error;
    },
  );
};
